import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import TasksTable from "../../components/CaseManagement/Components/TaskComponent";
import Sidebar from "../../components/CaseManagement/Components/Sidebar";
import InformationCard from "../../components/CaseManagement/Components/InformationCards";
import Navbar from "../../components/CaseManagement/Components/Navbar";
import LawyerCard from "../../components/CaseManagement/Components/LawyerCard";
import {
  CMSContext,
  CMSProvider,
} from "../../components/CaseManagement/context/CaseManagmentContext";
import DocumentManagement from "../../components/CaseManagement/Components/UpdateDocument";
import DashboardContent from "../../components/CaseManagement/Components/DashboardContent";
import { useDispatch, useSelector } from "react-redux";
import CaseDetailCard from "../../components/CaseManagement/Components/CaseDetailCard";
import { toast } from "react-toastify";
import { showInsufficientCreditsModal } from "../../actions/creditActions";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import ClientsPage from "../../components/CaseManagement/Components/ClientsPage";
import LawyersPage from "../../components/CaseManagement/Components/LawyersPage";
import CaseHistory from "../../components/CaseManagement/Components/CasesHistory";
import ReferenceCaseModal from "../../dialogs/ReferenceCaseDialog";
import { CaseReferenceTable } from "../../components/CaseManagement/Components/CaseReferenceTable";
import RightSideBar from "../../components/CaseManagement/Components/RightSideBar";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ComingSoon from "../../components/CaseManagement/Components/ComingSoon";
import { StatuteReferenceTable } from "../../components/CaseManagement/Components/StatuteReferenceTable";
import {
  getLawyersApi,
  getClientsApi,
  updateClientApi,
  updateCaseApi,
} from "../../services/api/caseManagementApi";
import LogoLoader from "../../components/UI/LogoLoader";
const initialDocuments = [
  {
    id: 1,
    title: "Evidence Report",
    description: "Primary evidence details",
    version: "1.1",
    uploadedBy: "John Smith",
    timestamp: "2024-11-11",
    comment: "Initial draft submitted",
    attachments: [
      {
        id: 11,
        title: "Evidence Review - Senior",
        description: "Reviewed by Senior Advocate",
        uploadedBy: "Robert Brown",
        timestamp: "2024-11-12",
      },
    ],
  },
];

// Main Dashboard Component
function CaseManagementSystem() {
  const [clientInfo, setClientInfo] = useState([]);
  const {
    cases,
    selectedCase,
    setSelectedCase,
    currentTab,
    setCurrentTab,
    advocates,
    setAdvocates,
    clients,
    setClients,
    isHistoryClicked,
    data,
    fetchCases,
    fetchCaseLoading,
    triggerTaskRefresh,
  } = useContext(CMSContext);

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const [caseInfo, setCaseInfo] = useState({});
  const [lawyersInfo, setLawyersInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState(initialDocuments);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const ownerId = userInfo._id;
  const credits = useSelector((state) => state.credit.credits); // Get credits from Redux store
  const userId = useSelector((state) => state.credit.userId);
  const dispatch = useDispatch();

  // for adding reference case below are there states, which would be removed in future
  const [open, setOpen] = useState(false);
  const [selectedReferenceCase, setSelectedReferenceCase] = useState("");
  const [caseNote, setCaseNote] = useState("");
  const [caseNoteError, setCaseNoteError] = useState("");
  const [isAddCaseLoading, setIsAddCaseLoading] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isRightSidebarVisible, setIsRightSidebarVisible] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  // New state for desktop sidebar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const toggleDesktopSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedReferenceCase("");
    setCaseNote("");
    setCaseNoteError("");
  };

  const handleNoteChange = (note) => {
    setCaseNote(note);
    setCaseNoteError(note.trim() ? "" : "Note is required");
  };

  const handleAddCaseToCaseManagement = async () => {
    if (!selectedReferenceCase || !caseNote.trim()) {
      setCaseNoteError("Note is required");
      return;
    }

    setIsAddCaseLoading(true);
    try {
      handleClose();
    } catch (error) {
      console.error("Error adding case:", error);
    } finally {
      setIsAddCaseLoading(false);
    }
  };

  // end of reference case modal data
  useEffect(() => {
    if (selectedCase) {
      setLoading(true);
      // Process case information
      setCaseInfo({
        caseTitle: selectedCase.caseTitle,
        caseDescription: selectedCase.caseDescription,
        filedBy: selectedCase.caseOwner,
        startDate: selectedCase.startDate,
        endDate: selectedCase?.endDate,
        createdBy: selectedCase.createdBy,
        status: selectedCase.status,
        ...selectedCase,
      });

      const matchedClients = clients?.filter((client) =>
        selectedCase?.clients?.includes(client._id),
      );

      setClientInfo(
        matchedClients.map((client) => ({
          name: client.name,
          phone: client.phone,
          email: client.email,
          cnic: client.cnic,
          profession: client.profession,
          address: client.address,
          ...client,
        })),
      );

      // Process lawyers information
      const matchedLawyers = advocates?.filter((advocate) =>
        selectedCase?.advocates?.some(
          (caseAdvocate) => caseAdvocate.id === advocate._id,
        ),
      );

      setLawyersInfo(
        matchedLawyers.map((lawyer) => ({
          name: lawyer.name,
          designation: lawyer.designation,
          ...lawyer,
        })),
      );
      setLoading(false);
    } else {
      // Reset state when no case is selected
      setCaseInfo({});
      setClientInfo({});
      setLawyersInfo([]);
      setLoading(false);
    }
  }, [selectedCase, advocates, clients]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 1) {
      fetchCases(selectedCase?._id, true);
      triggerTaskRefresh();
    } else {
      setSelectedCase(null);
    }
  };

  const fetchAdvocates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getLawyersApi(ownerId);
      const data = response.data;
      setAdvocates(data || []);
    } catch (error) {
      console.error("Error fetching advocates:", error);
    }
  }, [ownerId]);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getClientsApi(ownerId);
      const data = response.data;
      setClients(data || []); // Ensure it's always an array
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  }, [ownerId]);

  // Fetch cases and related data (advocates, clients)
  useEffect(() => {
    fetchAdvocates();
    fetchClients();
  }, []);

  const handleSaveClientInfo = async (updatedClient) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    try {
      const response = await updateClientApi(updatedClient._id, updatedClient);

      if (response.status === 200) {
        toast.success("Client updated successfully.");
        fetchClients();
      }
      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Update Client Information on case management page.",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      toast.error("Error updating client");
      // fetchClients();
      console.error("Error updating client:", error);
    }
  };
  const handleSaveCaseInfo = async (data) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    const updatedCase = {
      ...selectedCase,
      ...data,
    };
    // Remove unwanted keys
    delete updatedCase.lawyerDetails;
    delete updatedCase.clientDetails;
    try {
      const response = await updateCaseApi(caseInfo._id, updatedCase);

      if (response.status === 200) {
        toast.success("Case details updated successfully");
        fetchCases(true); // Refresh the cases after update
      } else {
        const errorData = await response.json();
        console.error("Error saving case:", errorData);
      }
      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Update Case Information on case management page.",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error saving case:", error);
    }
  };
  return (
    <>
      <Helmet>
        <title>LawGPT - Case Management System</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 50px)",
          boxSizing: "border-box",
          p: "12px 12px 0 12px",
          backgroundColor: "#F8FAFC",
          width: "100%",
        }}
      >
        <Navbar
          currentTab={currentTab}
          handleTabChange={handleTabChange}
          onClose={() => {
            setIsSidebarVisible(!isSidebarVisible);
          }}
        />
        {currentTab === 0 ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "calc(100vh - 150px)",
              gap: 1,
              bgcolor: "#F5F5F5",
              overflowY: "auto",
            }}
          >
            {" "}
            <DashboardContent />{" "}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "calc(100vh - 150px)",
              gap: 1,
              bgcolor: "#F5F5F5",
            }}
          >
            {/* Left Sidebar */}
            {isMobile ? (
              <Drawer
                anchor="left"
                open={isSidebarVisible}
                onClose={() => {
                  setIsSidebarVisible(!isSidebarVisible);
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#F7F7F7",
                    overflowY: "auto",
                    "::-webkit-scrollbar": {
                      width: "5px",
                    },
                    "::-webkit-scrollbar-track": {
                      background: "#fff",
                    },
                    "::-webkit-scrollbar-thumb": {
                      background: "#bbb",
                      borderRadius: "6px",
                    },
                  }}
                >
                  <Sidebar
                    toggleSidebar={() => {
                      setIsSidebarVisible(!isSidebarVisible);
                    }}
                    onClose={() => {
                      setIsSidebarVisible(!isSidebarVisible);
                    }}
                  />
                </Box>
              </Drawer>
            ) : (
              <Box
                sx={{
                  ...(!sidebarCollapsed && {
                    minWidth: "16%",
                    maxWidth: "16%",
                    width: "16%",
                  }),
                  bgcolor: "#F7F7F7",
                  overflowY: "auto",
                  "::-webkit-scrollbar": {
                    width: "5px",
                  },
                  "::-webkit-scrollbar-track": {
                    background: "#fff",
                  },
                  "::-webkit-scrollbar-thumb": {
                    background: "#bbb",
                    borderRadius: "6px",
                  },
                }}
              >
                <Sidebar
                  isCollapsed={sidebarCollapsed}
                  toggleSidebar={toggleDesktopSidebar}
                  onClose={() => {
                    setIsSidebarVisible(!isSidebarVisible);
                  }}
                />
              </Box>
            )}

            {/* Main Content */}
            <Box
              component="main"
              sx={{
                flex: 1,
                bgcolor: "#FFFFFF",
                borderRadius: "10px",
                px: { xs: 1, md: 2 },
                py: { xs: 1, md: 2 },
                boxSizing: "border-box",
                overflowY: "auto",
                "::-webkit-scrollbar": {
                  width: "5px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#fff",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#bbb",
                  borderRadius: "6px",
                },
              }}
            >
              {fetchCaseLoading ? (
                <Box
                  sx={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
                </Box>
              ) : (cases == null || cases.length == 0) && currentTab === 1 ? (
                <Box
                  display="flex"
                  boxSizing="border-box"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  bgcolor="#f9f9f9"
                  p={3}
                >
                  <AssignmentOutlinedIcon
                    sx={{ fontSize: 64, color: "#ccc", mb: 2 }}
                  />
                  <Typography variant="h5" color="textSecondary" gutterBottom>
                    No Case Selected
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    gutterBottom
                  >
                    Select a case from the list to view its details.
                  </Typography>
                </Box>
              ) : isHistoryClicked && currentTab === 1 ? (
                <CaseHistory />
              ) : currentTab === 1 ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <InformationCard
                      title="Client Information"
                      data={clientInfo}
                      onSave={(updatedData) => {
                        handleSaveClientInfo(updatedData);
                      }}
                      loading={loading}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CaseDetailCard
                      title="Case Information"
                      data={caseInfo}
                      onSave={handleSaveCaseInfo}
                      loading={loading}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <LawyerCard
                      title="Lawyers Information"
                      data={lawyersInfo}
                      loading={loading}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TasksTable />
                  </Grid>
                  <Grid item xs={12}>
                    <DocumentManagement
                      documents={documents}
                      setIsRightSidebarVisible={setIsRightSidebarVisible}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CaseReferenceTable />
                  </Grid>
                  <Grid item xs={12}>
                    <StatuteReferenceTable />
                  </Grid>
                </Grid>
              ) : currentTab === 2 ? (
                <ClientsPage />
              ) : currentTab === 3 ? (
                <LawyersPage />
              ) : currentTab === 4 ? (
                <ComingSoon />
              ) : null}
            </Box>

            {/* Right Sidebar */}
            {data &&
              (isMobile ? (
                <Drawer
                  anchor="right"
                  ModalProps={{
                    keepMounted: true,
                  }}
                  open={isRightSidebarVisible}
                  onClose={() => {
                    setIsRightSidebarVisible(!isRightSidebarVisible);
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      bgcolor: "#F7F7F7",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "5px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "#fff",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "#bbb",
                        borderRadius: "6px",
                      },
                    }}
                  >
                    <RightSideBar />
                  </Box>
                </Drawer>
              ) : (
                <Box
                  sx={{
                    minWidth: "18%",
                    maxWidth: "18%",
                    width: "18%",
                    height: "100%",
                    bgcolor: "#F7F7F7",
                    overflowY: "auto",
                    "::-webkit-scrollbar": {
                      width: "5px",
                    },
                    "::-webkit-scrollbar-track": {
                      background: "#fff",
                    },
                    "::-webkit-scrollbar-thumb": {
                      background: "#bbb",
                      borderRadius: "6px",
                    },
                  }}
                >
                  <RightSideBar />
                </Box>
              ))}
          </Box>
        )}
      </Box>
      {/* <Box
        sx={{
          position: "absolute",
          width: { xs: "100%", lg: "calc(100% - 45px)" },
          height: "calc(100vh - 50px)",
          top: "50px",
          left: { xs: "0px", lg: "45px" },
          backgroundColor: "transparent",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1,
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "relative",
            width: { xs: "100vw", lg: "calc(100vw - 45px)" },
            height: "calc(100vh - 50px)",
            backgroundColor: "transparent",
            display: "flex",
            flexDirection: "row",
            overflowY: "clip",
          }}
        >
          <ComingSoonModal />
        </Box>
      </Box> */}
      <ReferenceCaseModal
        open={open}
        onClose={handleClose}
        selectedReferenceCase={selectedReferenceCase}
        setSelectedReferenceCase={setSelectedReferenceCase}
        caseNote={caseNote}
        caseNoteError={caseNoteError}
        handleNoteChange={handleNoteChange}
        isAddCaseLoading={isAddCaseLoading}
        handleAddCaseToCaseManagement={handleAddCaseToCaseManagement}
      />
    </>
  );
}

export default () => (
  <CMSProvider>
    <CaseManagementSystem />
  </CMSProvider>
);
